import { useState, useMemo, useEffect } from 'react';
import {
  PlusIcon,
  FunnelIcon,
  CheckSquareIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/formatters';
import { format } from 'date-fns';
import {
  useRexelAllTransactions,
  Transaction,
} from '@/api/rexel/ship-and-debit/useRexelAllTransactions';

const DIVISION_OPTIONS = [
  { value: 'all', label: 'All Divisions' },
  { value: 'CQE', label: 'CQE' },
  { value: 'CRA', label: 'CRA' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

interface AvailableTransactionsSectionProps {
  workbenchIds: Set<string>;
  onAddToWorkbench: (transactions: Transaction[]) => void;
}

export function AvailableTransactionsSection({
  workbenchIds,
  onAddToWorkbench,
}: AvailableTransactionsSectionProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tradingPartnerFilter, setTradingPartnerFilter] = useState<string>('');
  const [shipTypeFilter, setShipTypeFilter] = useState<string>('');
  const [divisionFilter, setDivisionFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(100);

  const { data, isLoading, error } = useRexelAllTransactions({
    tradingPartner: tradingPartnerFilter || undefined,
    shipType: shipTypeFilter || undefined,
    division: divisionFilter,
    limit: 1000,
  });

  // Filter out transactions that are already in the workbench (client-side)
  // This avoids refetching when workbench changes
  const transactions = useMemo(
    () => (data?.transactions ?? []).filter((t) => !workbenchIds.has(t.id)),
    [data?.transactions, workbenchIds],
  );

  // Paginate the transactions - clamp currentPage to valid range
  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));
  const validCurrentPage = Math.min(currentPage, Math.max(0, totalPages - 1));
  const paginatedTransactions = useMemo(
    () =>
      transactions.slice(
        validCurrentPage * pageSize,
        (validCurrentPage + 1) * pageSize,
      ),
    [transactions, validCurrentPage, pageSize],
  );

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value));
    setCurrentPage(0);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [tradingPartnerFilter, shipTypeFilter, divisionFilter]);

  // Extract unique trading partners and ship types for filter dropdowns
  const { tradingPartners, shipTypes } = useMemo(() => {
    const partners = new Set<string>();
    const types = new Set<string>();

    transactions.forEach((t) => {
      if (t.tradingPartner) partners.add(t.tradingPartner);
      if (t.shipType) types.add(t.shipType);
    });

    return {
      tradingPartners: Array.from(partners).sort(),
      shipTypes: Array.from(types).sort(),
    };
  }, [transactions]);

  const handleSelectAll = () => {
    const pageIds = paginatedTransactions.map((t) => t.id);
    const allPageSelected = pageIds.every((id) => selectedIds.has(id));

    if (allPageSelected) {
      // Deselect all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddSelected = () => {
    const selected = transactions.filter((t) => selectedIds.has(t.id));
    onAddToWorkbench(selected);
    setSelectedIds(new Set());
  };

  const handleAddSingle = (transaction: Transaction) => {
    onAddToWorkbench([transaction]);
  };

  const handleAddAllFiltered = () => {
    onAddToWorkbench(transactions);
    setSelectedIds(new Set());
  };

  const isAllPageSelected =
    paginatedTransactions.length > 0 &&
    paginatedTransactions.every((t) => selectedIds.has(t.id));
  const isSomePageSelected = paginatedTransactions.some((t) =>
    selectedIds.has(t.id),
  );
  const isSomeSelected = selectedIds.size > 0;

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Available Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive">
            Failed to load transactions. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Available Transactions</CardTitle>
            <CardDescription>
              Select transactions to add to your workbench
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isSomeSelected && (
              <Button size="sm" onClick={handleAddSelected}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Selected ({selectedIds.size})
              </Button>
            )}
            {transactions.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddAllFiltered}
              >
                <CheckSquareIcon className="h-4 w-4 mr-2" />
                Add All ({transactions.length})
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mt-4">
          <FunnelIcon className="h-4 w-4 text-muted-foreground" />

          <Select
            value={tradingPartnerFilter || 'all'}
            onValueChange={(v) => setTradingPartnerFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="h-8 w-[200px]">
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {tradingPartners.map((partner) => (
                <SelectItem key={partner} value={partner}>
                  {partner}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={shipTypeFilter || 'all'}
            onValueChange={(v) => setShipTypeFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue placeholder="All Ship Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ship Types</SelectItem>
              {shipTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  Ship Type {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={divisionFilter} onValueChange={setDivisionFilter}>
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue placeholder="All Divisions" />
            </SelectTrigger>
            <SelectContent>
              {DIVISION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {data?.pagination && (
            <Badge variant="secondary" className="ml-auto">
              {data.pagination.total} total transactions
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <LoadingTable />
        ) : transactions.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            No available transactions found.
          </div>
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={
                        isAllPageSelected
                          ? true
                          : isSomePageSelected
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all on page"
                    />
                  </TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>SO #</TableHead>
                  <TableHead>Ship Type</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Claim Amount</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTransactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    className={
                      selectedIds.has(transaction.id) ? 'bg-muted/50' : ''
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(transaction.id)}
                        onCheckedChange={() => handleSelectRow(transaction.id)}
                        aria-label={`Select transaction ${transaction.rexelSalesOrderNumber}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {transaction.tradingPartner || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {transaction.fileThreeLetterDivision}
                      </div>
                    </TableCell>
                    <TableCell>{transaction.rexelSalesOrderNumber}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {transaction.shipType || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {transaction.orderDate
                        ? format(new Date(transaction.orderDate), 'MM/dd/yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {transaction.customerName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {transaction.customerShipToCity},{' '}
                        {transaction.customerShipToStateProvince}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium truncate max-w-[150px]">
                        {transaction.itemInternalCode}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                        {transaction.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(transaction.claimAmount, {
                        locale: 'en-CA',
                        currency: 'CAD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddSingle(transaction)}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Rows per page:
                </span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-4">
                  Showing {validCurrentPage * pageSize + 1}-
                  {Math.min(
                    (validCurrentPage + 1) * pageSize,
                    transactions.length,
                  )}{' '}
                  of {transactions.length}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={validCurrentPage === 0}
                >
                  <CaretLeftIcon className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {validCurrentPage + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={validCurrentPage >= totalPages - 1}
                >
                  <CaretRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]">
            <Skeleton className="h-4 w-4" />
          </TableHead>
          <TableHead>Supplier</TableHead>
          <TableHead>SO #</TableHead>
          <TableHead>Ship Type</TableHead>
          <TableHead>Order Date</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Item</TableHead>
          <TableHead className="text-right">Claim Amount</TableHead>
          <TableHead className="w-[80px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton className="h-4 w-4" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[120px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[80px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[60px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[80px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[100px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[100px]" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-[80px] ml-auto" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-8 w-8" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
